import React, { useState, useEffect } from 'react';
import './SimulationsList.css';

const SimulationsList = ({ portfolio, onCreateNew, onViewResults, refreshTrigger }) => {
    const [simulations, setSimulations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionInProgress, setActionInProgress] = useState({});
    const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);

    useEffect(() => {
        if (portfolio?.id) {
            loadSimulations();
        }
    }, [portfolio, refreshTrigger]);

    // Автообновление статусов симуляций каждые 3 секунды
    useEffect(() => {
        if (!portfolio?.id) return;

        const interval = setInterval(() => {
            // Проверяем есть ли активные симуляции
            const hasRunningSimulations = simulations.some(sim =>
                sim.status === 'running' || sim.status === 'pending'
            );

            if (hasRunningSimulations) {
                loadSimulations(true); // автообновление
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [portfolio?.id, simulations]);

    const loadSimulations = async (isAutoRefresh = false) => {
        try {
            if (isAutoRefresh) {
                setIsAutoRefreshing(true);
            } else {
                setLoading(true);
            }
            console.log('Loading simulations for portfolio:', portfolio.id);
            const url = `/api/v1/portfolios/${portfolio.id}/simulations`;
            console.log('Fetching URL:', url);

            const response = await fetch(url);
            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Response error:', errorText);
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('Response data:', data);
            console.log('Simulations array:', data.simulations);
            setSimulations(data.simulations || []);
        } catch (error) {
            console.error('Error loading simulations:', error);
            if (!isAutoRefresh) {
                setError(`Ошибка загрузки симуляций: ${error.message}`);
            }
        } finally {
            if (isAutoRefresh) {
                setIsAutoRefreshing(false);
            } else {
                setLoading(false);
            }
        }
    };

    const handleStartSimulation = async (simulationId) => {
        setActionInProgress(prev => ({ ...prev, [simulationId]: 'starting' }));
        try {
            const response = await fetch(`/api/v1/simulations/${simulationId}/start`, {
                method: 'PUT'
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            await loadSimulations(); // Refresh list
        } catch (error) {
            console.error('Error starting simulation:', error);
            setError(`Ошибка запуска симуляции: ${error.message}`);
        } finally {
            setActionInProgress(prev => ({ ...prev, [simulationId]: null }));
        }
    };

    const handleStopSimulation = async (simulationId) => {
        setActionInProgress(prev => ({ ...prev, [simulationId]: 'stopping' }));
        try {
            const response = await fetch(`/api/v1/simulations/${simulationId}/stop`, {
                method: 'PUT'
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            await loadSimulations(); // Refresh list
        } catch (error) {
            console.error('Error stopping simulation:', error);
            setError(`Ошибка остановки симуляции: ${error.message}`);
        } finally {
            setActionInProgress(prev => ({ ...prev, [simulationId]: null }));
        }
    };

    const handleDeleteSimulation = async (simulationId, simulationName) => {
        if (!window.confirm(`Вы уверены, что хотите удалить симуляцию "${simulationName}"?`)) {
            return;
        }

        setActionInProgress(prev => ({ ...prev, [simulationId]: 'deleting' }));
        try {
            const response = await fetch(`/api/v1/simulations/${simulationId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            await loadSimulations(); // Refresh list
        } catch (error) {
            console.error('Error deleting simulation:', error);
            setError(`Ошибка удаления симуляции: ${error.message}`);
        } finally {
            setActionInProgress(prev => ({ ...prev, [simulationId]: null }));
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { text: 'Ожидает', class: 'status-pending' },
            running: { text: 'Выполняется', class: 'status-running' },
            completed: { text: 'Завершена', class: 'status-completed' },
            failed: { text: 'Ошибка', class: 'status-failed' },
            cancelled: { text: 'Отменена', class: 'status-cancelled' }
        };

        const config = statusConfig[status] || { text: status, class: 'status-unknown' };
        return <span className={`status-badge ${config.class}`}>{config.text}</span>;
    };

    const getProgressBar = (simulation) => {
        if (simulation.status === 'running') {
            return (
                <div className="progress-bar">
                    <div
                        className="progress-fill"
                        style={{ width: `${simulation.progress || 0}%` }}
                    ></div>
                    <span className="progress-text">{Math.round(simulation.progress || 0)}%</span>
                </div>
            );
        }
        return null;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('ru-RU');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleString('ru-RU');
    };

    const renderActionButtons = (simulation) => {
        const inProgress = actionInProgress[simulation.id];

        return (
            <div className="simulation-actions">
                {simulation.status === 'pending' && (
                    <button
                        onClick={() => handleStartSimulation(simulation.id)}
                        disabled={inProgress}
                        className="btn btn-start"
                    >
                        {inProgress === 'starting' ? 'Запуск...' : 'Запустить'}
                    </button>
                )}

                {simulation.status === 'running' && (
                    <button
                        onClick={() => handleStopSimulation(simulation.id)}
                        disabled={inProgress}
                        className="btn btn-stop"
                    >
                        {inProgress === 'stopping' ? 'Остановка...' : 'Остановить'}
                    </button>
                )}

                {simulation.status === 'completed' && (
                    <button
                        onClick={() => onViewResults(simulation)}
                        className="btn btn-results"
                    >
                        Результаты
                    </button>
                )}

                {simulation.status !== 'running' && (
                    <button
                        onClick={() => handleDeleteSimulation(simulation.id, simulation.name)}
                        disabled={inProgress}
                        className="btn btn-delete"
                    >
                        {inProgress === 'deleting' ? 'Удаление...' : 'Удалить'}
                    </button>
                )}
            </div>
        );
    };

    const renderSimulationFeatures = (simulation) => {
        const features = [];

        if (simulation.enable_rebalancing) {
            features.push('Ребалансировка');
        }
        if (simulation.enable_margin) {
            features.push('Маржа');
        }
        if (simulation.enable_buy_borrow_die) {
            features.push('Buy-Borrow-Die');
        }
        if (simulation.calculate_inflation) {
            features.push('Учет инфляции');
        }

        if (features.length === 0) {
            return <span className="features-none">Базовая стратегия</span>;
        }

        return (
            <div className="simulation-features">
                {features.map(feature => (
                    <span key={feature} className="feature-tag">{feature}</span>
                ))}
            </div>
        );
    };

    const renderResults = (simulation) => {
        // Проверяем что симуляция завершена
        if (simulation.status !== 'completed') {
            return null;
        }

        // Проверяем наличие key_metrics
        if (!simulation.key_metrics || Object.keys(simulation.key_metrics).length === 0) {
            return (
                <div className="simulation-results-summary">
                    <div className="result-item warning">
                        <span className="result-label">⚠️ Ошибка данных</span>
                        <span className="result-value">Результаты симуляции некорректны</span>
                    </div>
                </div>
            );
        }

        // Рендерим key_metrics
        return (
            <div className="simulation-results-summary">
                {Object.entries(simulation.key_metrics).map(([key, metric]) => {
                    if (!metric || metric.value === null || metric.value === undefined) {
                        return null;
                    }

                    // Форматируем значение
                    let displayValue = metric.value;
                    if (metric.format === '0.00%') {
                        displayValue = `${metric.value.toFixed(2)}%`;
                    } else if (metric.format === '0,0') {
                        displayValue = displayValue.toLocaleString();
                    } else if (metric.format === '0.000') {
                        displayValue = displayValue.toFixed(3);
                    } else if (metric.data_type === 'currency') {
                        displayValue = `$${displayValue.toLocaleString()}`;
                    }

                    // Определяем класс для цвета (положительное/отрицательное значение)
                    let valueClass = 'result-value';
                    if (typeof metric.value === 'number') {
                        if (metric.value > 0 && key.includes('return')) {
                            valueClass += ' positive';
                        } else if (metric.value < 0) {
                            valueClass += ' negative';
                        }
                    }

                    return (
                        <div key={key} className="result-item">
                            <span className="result-label">{metric.label}:</span>
                            <span className={valueClass}>{displayValue}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (!portfolio) {
        return (
            <div className="simulations-list">
                <div className="empty-state">
                    <p>Выберите портфель для просмотра симуляций</p>
                </div>
            </div>
        );
    }

    return (
        <div className="simulations-list">
            <div className="simulations-header">
                <div className="header-title">
                    <h3>Симуляции для портфеля "{portfolio.name}"</h3>
                    {isAutoRefreshing && (
                        <span className="auto-refresh-indicator">
                            🔄 Обновление...
                        </span>
                    )}
                </div>
                <button onClick={onCreateNew} className="btn btn-primary">
                    Новая симуляция
                </button>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                    <button onClick={loadSimulations} className="retry-btn">
                        Попробовать снова
                    </button>
                </div>
            )}

            {loading ? (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Загрузка симуляций...</p>
                </div>
            ) : simulations.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📊</div>
                    <h4>Симуляций пока нет</h4>
                    <p>Создайте первую симуляцию для анализа портфеля</p>
                    <button onClick={onCreateNew} className="btn btn-primary">
                        Создать симуляцию
                    </button>
                </div>
            ) : (
                <div className="simulations-grid">
                    {simulations.map(simulation => (
                        <div key={simulation.id} className={`simulation-card ${simulation.status}`}>
                            <div className="simulation-header">
                                <h4>{simulation.name}</h4>
                                {getStatusBadge(simulation.status)}
                            </div>

                            {simulation.description && (
                                <p className="simulation-description">
                                    {simulation.description}
                                </p>
                            )}

                            <div className="simulation-info">
                                <div className="info-row">
                                    <span className="info-label">Период:</span>
                                    <span className="info-value">
                                        {formatDate(simulation.start_date)} — {formatDate(simulation.end_date) || 'Не указан'}
                                    </span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Создана:</span>
                                    <span className="info-value">{formatDateTime(simulation.created_at)}</span>
                                </div>
                                {simulation.completed_at && (
                                    <div className="info-row">
                                        <span className="info-label">Завершена:</span>
                                        <span className="info-value">{formatDateTime(simulation.completed_at)}</span>
                                    </div>
                                )}
                            </div>

                            {renderSimulationFeatures(simulation)}
                            {getProgressBar(simulation)}
                            {renderResults(simulation)}
                            {renderActionButtons(simulation)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SimulationsList;
